import { useEffect, useState, Suspense, lazy, useCallback } from 'react';
import { Switch, Route } from 'wouter';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { PageLoader } from '@/components/ui/page-loader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import DashboardLayout from '@/components/layout/dashboard-layout';
import { ModeProvider } from '@/lib/ModeContext';
import { CommandPalette } from '@/components/ui/command-palette';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useCurrentUser } from '@/hooks/use-current-user';
import { ThemeProvider } from '@/lib/theme';

// Lazy-load the allocator review surface for route-level code splitting.
const Login = lazy(() => import('@/pages/login'));
const DecisionRoom = lazy(() => import('@/pages/decision-room'));
const Strategy = lazy(() => import('@/pages/strategy'));
const PerformanceRisk = lazy(() => import('@/pages/performance-risk'));
const Operations = lazy(() => import('@/pages/operations'));
const EvidenceRoom = lazy(() => import('@/pages/evidence-room'));
const Governance = lazy(() => import('@/pages/governance'));
const NotFound = lazy(() => import('@/pages/not-found'));

function AuthWrapper({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading, error, isError } = useCurrentUser();

  // Show loading screen with proper background
  if (isLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  // Handle auth errors or no user with smooth transition
  if (isError || error || !user) {
    return (
      <div className="min-h-screen bg-navy-900 transition-all duration-300">
        <Login />
      </div>
    );
  }

  // User is authenticated, show dashboard with smooth transition
  return <div className="min-h-screen bg-navy-900 transition-all duration-300">{children}</div>;
}

function Router() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const openCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true);
  }, []);

  useEffect(() => {
    const handleOpenCommandPalette = () => {
      openCommandPalette();
    };

    window.addEventListener('qf:open-command-palette', handleOpenCommandPalette);
    return () => {
      window.removeEventListener('qf:open-command-palette', handleOpenCommandPalette);
    };
  }, [openCommandPalette]);

  // Global keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'k',
        modifiers: ['meta'],
        action: openCommandPalette,
        description: 'Open command palette',
      },
      {
        key: 'k',
        modifiers: ['ctrl'],
        action: openCommandPalette,
        description: 'Open command palette',
      },
    ],
  });

  return (
    <AuthWrapper>
      <DashboardLayout>
        <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/" component={DecisionRoom} />
            <Route path="/strategy" component={Strategy} />
            <Route path="/performance" component={PerformanceRisk} />
            <Route path="/operations" component={Operations} />
            <Route path="/evidence" component={EvidenceRoom} />
            <Route path="/governance" component={Governance} />

            {/* Preserve old bookmarks while keeping credential and control UI out of allocator review. */}
            <Route path="/strategies" component={Strategy} />
            <Route path="/risk" component={PerformanceRisk} />
            <Route path="/execution" component={Operations} />
            <Route path="/connections" component={Operations} />
            <Route path="/audit" component={EvidenceRoom} />
            <Route path="/trade-ledger" component={EvidenceRoom} />
            <Route path="/approvals" component={Governance} />
            <Route path="/testing" component={EvidenceRoom} />
            <Route path="/settings" component={Governance} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </DashboardLayout>
    </AuthWrapper>
  );
}

function App() {
  // Add global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.warn('Unhandled promise rejection:', event.reason);
      // Prevent the default behavior (console error)
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ModeProvider>
          <TooltipProvider>
            <ErrorBoundary>
              <Toaster />
              <Router />
            </ErrorBoundary>
          </TooltipProvider>
        </ModeProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
